import React from 'react';
import {addFromColumn, averageColumn, sortCurrency, sortDate, sortNumber, toCurrency} from './functions';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import {tableStyles} from '../shared/styles-table';

/**
 * Assemble dynamic columns for Sales Comparison Summary for all Locations
 *
 * @param yearsBack
 * @param yearsList
 * @param list
 * @param tableData
 * @param iconFunction
 * @param iconEnabled
 * @return {*}
 */
export const getSalesComparisonSummaryColumns = (yearsBack, yearsList, list, tableData, iconFunction, iconEnabled) => {

    const iconStyle = (iconEnabled) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;

    // Create columns for Location Data
    const locationCols = {
        Header: 'Location',
        columns: [
            {
                Header: 'Store',
                accessor: 'location_id',
                sortMethod: (a, b) => sortNumber(a, b, 'ASC'),
                Cell: row => (
                    <span>
                        <span>{row.row.location_id}</span>
                        <DetailIcon
                            onClick={(e) => iconFunction(e, row.row)}
                            style={iconStyle}
                        />
                    </span>
                ),
                style: tableStyles.right,
            },
            {
                Header: 'Name',
                accessor: 'location_name',
            },
            {
                Header: 'State',
                accessor: 'state',
                style: tableStyles.tdMax
            },
            {
                Header: 'DBA',
                accessor: 'dba_name',
            },
            {
                Header: 'Zip',
                accessor: 'zipcode',
                headerStyle: tableStyles.thLast,
            }
        ]
    };

    // Create columns for Gross Totals (base + all compared years)
    let grossCols = createSalesComparisonColumns('Gross', 'Gross Totals', tableData, yearsBack, yearsList);

    // Create columns for Net Totals (base + all compared years)
    let netCols = createSalesComparisonColumns('Net', 'Net Totals', tableData, yearsBack, yearsList);

    // Create columns for Average $ Totals (base + all compared years)
    let averageCols = createSalesComparisonColumns('Average', 'Average Order $', tableData, yearsBack, yearsList);

    return [
        locationCols,
        grossCols,
        netCols,
        averageCols
    ];
};

/**
 * Assemble dynamic columns for Sales Comparison Detail for selected Location
 *
 * @param yearsBack
 * @param yearsList
 * @param list
 * @param tableData
 * @return {*[]}
 */
export const getSalesComparisonDetailColumns = (yearsBack, yearsList, list, tableData) => {

    // Create static columns for Location Data
    const locationCols = {
        Header: '',
        columns: [
            {
                Header: 'Date',
                accessor: 'date',
                sortMethod: (a, b) => sortDate(a, b, 'mm-dd-yyyy', '-'),
                filterable: false,
            }
        ]
    };

    // Create columns for Gross Totals (base + all compared years)
    let grossCols = createSalesComparisonColumns('Gross', 'Gross Totals', tableData, yearsBack, yearsList);

    // Create columns for Net Totals (base + all compared years)
    let netCols = createSalesComparisonColumns('Net', 'Net Totals', tableData, yearsBack, yearsList);

    // Create columns for Average $ Totals (base + all compared years)
    let averageCols = createSalesComparisonColumns('Average', 'Average Order $', tableData, yearsBack, yearsList);

    return [
        locationCols,
        grossCols,
        netCols,
        averageCols
    ];
};

/**
 * Generate table headers with base and compare columns
 *
 * @param colType
 * @param baseHeader
 * @param tableData
 * @param yearsBack
 * @param yearsList
 * @return Object
 */
const createSalesComparisonColumns = (colType, baseHeader, tableData, yearsBack, yearsList) => {
    const styles = {
        tdAgg: {width: 115, whiteSpace: 'nowrap', textOverflow: 'initial'},
        thAgg: {width: 115, alignSelf: 'top', verticalAlign: 'top', textAlign: 'left', whiteSpace: 'nowrap', lineHeight: '1.2'}
    };
    // Create column object with base item
    let colsArr = {
        Header: baseHeader,
        columns: [
            {
                Header: createYearsHeader('Base Year', 0, yearsList),
                accessor: 'base' + colType,
                sortMethod: (a, b) => sortCurrency(a, b),
                filterable: false,
                style: {...tableStyles.right, ...styles.tdAgg},
                headerStyle: styles.thAgg,
                width: 115,
                Footer: (
                    <span style={tableStyles.footer}>
                        {
                            (colType === 'Average')
                                ? toCurrency(averageColumn(tableData, 'base' + colType))
                                : toCurrency(addFromColumn(tableData, 'base' + colType))
                        }
                    </span>
                )
            }
        ]
    };
    let yearsStr = '';
    let headerStyle = {};
    // Add a column for each year back to compare
    for (let i = 1; i < yearsBack; i++) {
        yearsStr = (i === 1) ? '1 Year Back' : i + ' Years Back';
        headerStyle = (i === (yearsBack - 1)) ? {...tableStyles.thLast, ...styles.thAgg} : styles.thAgg;
        let compareRow = {
            Header: createYearsHeader(yearsStr, i, yearsList),
            accessor: 'compare' + colType + i,
            sortMethod: (a, b) => sortCurrency(a, b),
            filterable: false,
            style: {...tableStyles.right, ...styles.tdAgg},
            headerStyle: headerStyle,
            width: 115,
            Footer: (
                <span style={tableStyles.footer}>
                    {
                        (colType === 'Average')
                            ? toCurrency(averageColumn(tableData, 'compare' + colType + i))
                            : toCurrency(addFromColumn(tableData, 'compare' + colType + i))
                    }
                </span>
            )
        };
        colsArr.columns.push(compareRow);
    }
    return colsArr;
};

/**
 * Assemble headers for CSV
 *
 * @param tableType
 * @param yearsBack
 * @param yearsList
 * @return {*[]}
 */
export const getSalesComparisonCSVHeaders = (tableType, yearsBack, yearsList) => {
    let arr;
    if (tableType === 'summary') {
        arr = [
            {label: 'Store', key: 'location_id'},
            {label: 'Name', key: 'location_name'},
            {label: 'State', key: 'state'},
            {label: 'DBA', key: 'dba_name'},
            {label: 'Zip', key: 'zipcode'},
        ];
    } else if (tableType === 'detail') {
        arr = [
            {label: 'Date', key: 'date'},
        ];
    }
    arr.push({
        label: 'Gross Totals - ' + getYearString(yearsList, 0, 'start') + ' to ' + getYearString(yearsList, 0, 'end'),
        key: 'baseGross'
    });
    for (let g = 1; g < yearsBack; g++) {
        let gBack = (g === 1) ? '1 Year Back - ' : g + ' Years Back - ';
        arr.push({
            label: 'Gross Totals ' + gBack + getYearString(yearsList, g, 'start') + ' to ' + getYearString(yearsList, g, 'end'),
            key: 'compareGross' + g
        });
    }
    arr.push({label: 'Net Base Year', key: 'baseNet'});
    for (let n = 1; n < yearsBack; n++) {
        let nBack = (n === 1) ? '1 Year Back - ' : n + ' Years Back - ';
        arr.push({
            label: 'Net Totals ' + nBack + getYearString(yearsList, n, 'start') + ' to ' + getYearString(yearsList, n, 'end'),
            key: 'compareNet' + n
        });
    }
    arr.push({label: 'Average Order $ - ', key: 'baseAverage'});
    for (let a = 1; a < yearsBack; a++) {
        let aBack = (a === 1) ? '1 Year Back - ' : a + ' Years Back - ';
        arr.push({
            label: 'Average Order $ - ' + aBack + getYearString(yearsList, a, 'start') + ' to ' + getYearString(yearsList, a, 'end'),
            key: 'compareAverage' + a
        });
    }
    return arr;
};

/**
 * Create a common header for Compare columns with HTML
 *
 * @param string
 * @param index
 * @param yearsBack
 * @return {*}
 */
const createYearsHeader = (string, index, yearsBack) => {
    let start = getYearString(yearsBack, index, 'start');
    let end = getYearString(yearsBack, index, 'end');
    return (
        <span>{string}<br/>{start} - <br/>{end}</span>
    )
};

/**
 * Get a year value from the years list
 *
 * @param list
 * @param index
 * @param key
 * @return {*}
 */
const getYearString = (list, index, key) => {
    if (list && list[index] && list[index][key]) {
        return list[index][key];
    }
    return '';
};



// WEBPACK FOOTER //
// ./src/Fuse/shared/salesComparisonReportFunctions.js